import * as React from "react";
import { Infinite, ICellProps } from "components/infinite";
import { List, Map } from "immutable";
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer";
import { merge, $ } from "glamor";
import { ColDef } from "ag-grid-community";
import {
    Item,
    SMU_FILTER,
    FILTER_KEY,
    ALLOCATION_DIMENSION_ID_PREFIX,
    UnitsEntryType,
} from "constants/product-collection";
import { theme } from "styles/theme";
import * as PropTypes from "prop-types";
import { getTileViewCellDimensions, getValueFormatter, IDisplayFields } from "helpers/product-collection";
import { NODE } from "helpers/collection";
import { AllocationBag, IAllocationDimension } from "helpers/allocation-dimension";
import { IDoor } from "helpers/door-collection";
import { ProductCollectionTileViewGroupState } from "./product-collection-tile-view-group-state";
import { IAddItem } from "actions/collections";
import { IGroupKey, GroupKeyType, IGroupProps } from "components/infinite/infinite-interface";
import { IGridParamsQueryParam } from "../product-collection-grid-view/tool-panels";
import { ICurrency } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers/pinned-row-constants";
import { ITileSettings } from "components/product-collection/manage-tile-view/types";
import { safeParseInt } from "lib/number";
import { ProductCollectionCard } from "./product-collection-card";
import { memoizedGetNewDimensionFilters } from "../product-collection-grid-view/product-collection-grid-view-helper";
import { getSortModelFromApi } from "helpers/ag-grid";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { delimiter } from "components/infinite/infinite-hocs/group-hoc";
import { groupItems } from "./helpers/groupItems";
import { IGridItem } from "helpers/legacy-cart/line-items/items-conversion-helper";

const groupDisplayInfo = {
    height: 80,
};

const TILE_VIEW_CONTAINER_MAX_WIDTH = 108000;
const PADDING_16 = 16;

interface IProps {
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    cardViewFields: IDisplayFields;
    shopViewFields?: IDisplayFields;
    schema: any; // all product fields
    doors: IDoor[];
    dataEntryType?: UnitsEntryType;
    editable: boolean;
    filterAssortmentByBrand?: string;
    filterAssortmentBySMU?: string;
    allItems?: List<any>; // List of all items
    items: List<any>; // List of items in the view. This should ideally be List<IItem>
    snapshotItems: List<any>;
    aggregationData: Map<string, Map<string, any>>;
    itemsId?: string;
    onLinkProductToItem: (id: string) => void;
    onMoveItem: (itemsId: string, index: number, destinationIndex: number, rowId: string) => void;
    onOpenDeleteConfirmation: (itemsid, itemIndex) => void;
    onOpenProductModal: (id) => void;
    onLoadMore?: () => void;
    hasMoreResults?: boolean;
    searchableFields: string[][];
    searchTerm?: string;
    bulkAddingItems: boolean;
    isFetchingMoreRecords?: boolean;
    allowedValueFields: any;
    brandEntities: any;
    rowGroup: any;
    gridParams: IGridParamsQueryParam;
    dimensionsPanelApplied: DimensionsPanelReduxState;
    gridApi: any;
    groupHeaders: string[];
    groupItemsOverride?: (items: List<IGridItem>) => any; // Override with your own custom grouping logic for items.
    groupRendererOverride?: (props: IGroupProps) => React.ReactNode; // Override with your own group/pivot data calculation, a.k.a row group headers.
    onAddItem: (params: IAddItem) => void;
    collectionId: string;
    formulaFields: IDisplayFields;
    allocationDimensions: IAllocationDimension[];
    dimensionFilters: string[][];
    allowDuplicateProduct: boolean;
    merchantCurrency?: ICurrency;
    tileSettings?: ITileSettings; // dictates the sub components of each tile.
    rawItemDisplayProperty?: ColDef[];
    tileViewSize?: string;
    openSizingGrid?: (lineItemId: string, refresh?: boolean) => void;
    onClickProductName?: (productId: string) => void;
    getProductNameLink?: (productId: string) => string;
}

interface IState {
    groupKey: IGroupKey[];
    groupDirection: "asc" | "desc";
}

/**
 * Generates a string joined with a delimiter constant to create a key to get aggregate data values
 * @param groupKeyValues
 */
export const generateCurrentGroupValueString = (groupKeyValues?: List<string>) => {
    const currentGroupValuesWithDelimiter = groupKeyValues?.join(delimiter);
    return currentGroupValuesWithDelimiter?.toLowerCase() ?? undefined;
};

export class ProductCollectionTileView extends React.Component<IProps, IState> {
    static contextTypes = {
        me: PropTypes.object.isRequired,
    };

    private styles = merge(
        {
            padding: "0",
            flex: "auto",
            position: "relative",
        },
        $(" .groupHeader", {
            backgroundColor: theme.palette.lightestGrey,
            borderRadius: "5px",
            margin: "0 4px",
            minHeight: "72px",
            overflow: "auto",
        }),
        $(" .groupHeader h3", {
            margin: "0",
            padding: `0 ${PADDING_16}px`,
            fontSize: "28px",
            lineHeight: "72px",
        }),
        $(" .groupTitle", {
            paddingLeft: "20px",
            height: "36px",
            lineHeight: "36px",
            fontSize: "17px",
        }),
        $(" .groupStats", {
            display: "flex",
        }),
        $(" .groupStats_level1", {
            paddingLeft: `${PADDING_16}px`,
        }),
        $(" .groupStats_level2", {
            paddingLeft: "32px",
        }),
        $(" .groupStats_level3", {
            paddingLeft: "48px",
        }),
        $(" .groupStat", {
            padding: "0 20px",
        }),
        $(" .groupStatTitle", {
            fontWeight: "bold",
        }),
        $(" .innerScrollContainer", {
            padding: `${PADDING_16}px`,
        }),
    );

    constructor(props) {
        super(props);
        this.state = {
            ...this.getParams(props),
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (nextProps.rowGroup !== this.props.rowGroup) {
            this.setState(this.getParams(nextProps));
        }
    }

    getGroupedItems = (items, groupKey, groupDirection) => {
        const { schema, allocationDimensions, gridApi } = this.props;
        const currentGroupKey = groupKey?.[0];

        if (!items || !groupDirection || !groupKey) {
            return {
                items,
                groupKey,
                groupDirection,
                isGrouped: !!currentGroupKey,
            };
        }

        const groupedItems = groupItems(items, schema, allocationDimensions, groupKey, gridApi);

        // Sort group
        return {
            items,
            groupKey,
            groupDirection,
            isGrouped: !!currentGroupKey,
            groupedItems,
        };
    };

    render() {
        const {
            allocationDimensions,
            allowedValueFields,
            bulkAddingItems,
            dimensionFilters,
            isFetchingMoreRecords,
            editable,
            gridApi,
            filterAssortmentBySMU,
            items,
            allItems,
            snapshotItems,
            searchableFields,
            searchTerm,
            onOpenProductModal,
            groupHeaders,
            schema,
            tileSettings,
            hasMoreResults,
            onLoadMore,
            tileViewSize,
            groupItemsOverride,
        } = this.props;

        let filters: Map<string, string> = Map();

        if (filterAssortmentBySMU) {
            switch (filterAssortmentBySMU) {
                case SMU_FILTER.SMUOnly:
                case SMU_FILTER.ExcludeSMU:
                    filters = filters.set(FILTER_KEY.ProductID, filterAssortmentBySMU);
                    break;
                case SMU_FILTER.All:
                default:
                    filters = filters.delete(FILTER_KEY.ProductID);
                    break;
            }
        }

        const isSortable =
            editable &&
            gridApi?.columnModel?.columnApi &&
            !gridApi.destroyCalled &&
            !gridApi.filterManager.advancedFilterPresent &&
            getSortModelFromApi(gridApi.columnModel.columnApi).length === 0;
        const { groupKey, groupDirection } = this.state;

        return (
            <div {...this.styles}>
                <AutoSizer>
                    {({ width, height }) => (
                        <Infinite
                            allocationDimensions={allocationDimensions}
                            comparator={allowedValueFields}
                            containerHeight={height}
                            containerWidth={width}
                            centered={true}
                            dimensionFilters={dimensionFilters}
                            filters={filters}
                            groupKey={groupKey}
                            groupDirection={groupDirection}
                            isFetching={bulkAddingItems || isFetchingMoreRecords}
                            items={items}
                            allItems={allItems}
                            snapshotItems={snapshotItems}
                            itemIdPath={Item.FIELDS.ID}
                            maxWidth={TILE_VIEW_CONTAINER_MAX_WIDTH}
                            onGetGroupDisplayInfo={groupDisplayInfo}
                            searchableFields={searchableFields}
                            searchTerm={searchTerm}
                            sortable={isSortable}
                            onDragEnd={this.handleMoveItem}
                            onGetCellDisplayInfo={getTileViewCellDimensions(tileViewSize, tileSettings)}
                            cellRenderer={this.handleCellRender}
                            groupItemsOverride={groupItemsOverride ?? this.getGroupedItems}
                            groupRenderer={this.props.groupRendererOverride ?? this.handleGroupRenderer}
                            onClick={onOpenProductModal}
                            groupHeaders={groupHeaders}
                            schema={schema}
                            tileSettings={tileSettings}
                            hasMoreResults={hasMoreResults}
                            onLoadMore={onLoadMore}
                            tileViewSize={tileViewSize}
                            visualOffset={this.getVisualOffset()}
                        />
                    )}
                </AutoSizer>
            </div>
        );
    }

    private getParams(props = this.props) {
        const { rowGroup } = props;
        const groupKey =
            rowGroup &&
            rowGroup.length &&
            rowGroup.map((group) => {
                if (group.includes(ALLOCATION_DIMENSION_ID_PREFIX)) {
                    return {
                        key: group,
                        type: GroupKeyType.AllocationDimension,
                    };
                }

                return {
                    key: ["fields", group, "string_value"],
                    type: GroupKeyType.Item,
                };
            });
        return {
            groupKey,
            groupDirection: "asc" as "asc" | "desc",
        };
    }

    /**
     * Returns a ProductCartTile component for each product on the shopping cart
     * If there's no data on the shopping cart then it shows an empty node.
     * @return React.Component
     */
    private handleCellRender = ({ data, isSortable, dataIndex, groupKeyValues }: ICellProps): React.ReactNode => {
        if (!data) {
            return <div className="node" />;
        }

        const {
            onAddItem,
            itemsId,
            onLinkProductToItem,
            collectionId,
            onOpenDeleteConfirmation,
            gridApi,
            editable,
            brandEntities,
            cardViewFields,
            shopViewFields,
            doors,
            formulaFields,
            allocationDimensions,
            gridParams,
            dimensionsPanelApplied,
            allowDuplicateProduct,
            merchantCurrency,
            tileSettings,
            rawItemDisplayProperty,
            tileViewSize,
            openSizingGrid,
            onClickProductName,
            getProductNameLink,
        } = this.props;
        const { groupKey } = this.state;

        return (
            <ProductCollectionCard
                allocationBagRef={this.props.allocationBagRef}
                allocationDimensions={allocationDimensions}
                allowDuplicateProduct={allowDuplicateProduct}
                brandEntities={brandEntities}
                cardViewFields={cardViewFields}
                shopViewFields={shopViewFields}
                collectionId={collectionId}
                data={data}
                dataIndex={dataIndex}
                dimensionFilters={memoizedGetNewDimensionFilters(
                    allocationDimensions,
                    gridParams,
                    dimensionsPanelApplied,
                )}
                doors={doors}
                dimensionsPanelApplied={dimensionsPanelApplied}
                editable={editable}
                formulaFields={formulaFields}
                gridApi={gridApi}
                gridParams={gridParams}
                groupKeyValues={groupKeyValues}
                groupedKeys={groupKey}
                itemsId={itemsId}
                isSortable={isSortable}
                merchantCurrency={merchantCurrency}
                onLinkProductToItem={onLinkProductToItem}
                onAddItem={onAddItem}
                onOpenDeleteConfirmation={onOpenDeleteConfirmation}
                openSizingGrid={openSizingGrid}
                rawItemDisplayProperty={rawItemDisplayProperty}
                tileSettings={tileSettings}
                tileViewSize={tileViewSize}
                onClickProductName={onClickProductName}
                getProductNameLink={getProductNameLink}
            />
        );
    };

    /**
     * To compute row group data and aggregation in row group headers.
     */
    private handleGroupRenderer = ({ groupKeyValues = List<string>(), groupKey = [] }): React.ReactNode => {
        // Looks like groupKey can contain string. In such case, it won't have `type` key.
        if (groupKey && !groupKey[0].type) {
            groupKey = [groupKey];
        }

        const { aggregationData, schema } = this.props;

        let rowGroupStatsComponents = null; // This is the aggregated data component (group statistics) that we see in row group. For example, total units etc.
        const currentGroupKey = groupKey[groupKeyValues.size - 1];
        let groupField = "";
        if (currentGroupKey) {
            if (currentGroupKey.type === GroupKeyType.Item) {
                groupField = currentGroupKey.key[1];
            } else if (currentGroupKey.type === GroupKeyType.AllocationDimension) {
                groupField = currentGroupKey.key;
            }
        }
        const aggValues = aggregationData.get(groupField);
        const currentGroupValue = generateCurrentGroupValueString(groupKeyValues);

        if (aggValues && aggValues.has(currentGroupValue)) {
            const currentGroup = aggValues.get(currentGroupValue);

            rowGroupStatsComponents = Object.keys(currentGroup)
                .filter((key) => key != NODE)
                .map((key) => {
                    const field = schema.find((fld) => fld.get("field_name") === key);
                    const value = currentGroup[key];
                    const column = currentGroup._node?.columnApi?.getColumn(key);
                    const valueFormatted = field
                        ? getValueFormatter(field.toJSON())({
                              node: currentGroup._node,
                              colDef: column?.colDef,
                              columnApi: currentGroup._node?.columnApi,
                              value,
                          })
                        : value;
                    const name = field ? field.get("display_name") : key;
                    return <ProductCollectionTileViewGroupState key={key} name={name} value={valueFormatted} />;
                });
        }

        return (
            <div className="groupHeader">
                <div className="groupTitle">
                    {groupKeyValues.size > 0 ? groupKeyValues.join(` ${delimiter} `) : "N/A"}
                </div>
                <div className="groupStats">{rowGroupStatsComponents}</div>
            </div>
        );
    };

    private handleMoveItem = ({ currentIndex, destinationIndex, rowId }) => {
        this.props.onMoveItem(this.props.itemsId, currentIndex, destinationIndex, rowId);
    };

    private getVisualOffset() {
        const assortmentHeaderHeight = document.getElementsByClassName("assortmentHeader")[0]?.clientHeight || 0;
        const assortmentNav = document.querySelector(".assortmentNav")?.parentElement;
        const assortmentNavHeight = assortmentNav
            ? safeParseInt(window?.getComputedStyle(assortmentNav)?.height, 0)
            : 0;
        const visualOffset = assortmentHeaderHeight + assortmentNavHeight + PADDING_16;
        return visualOffset;
    }
}
